// Typed API surface. Every call goes through `request<T>` so the error
// envelope from the FastAPI backend (`{error: {code, message, details, request_id}}`)
// is unwrapped consistently.

import type {
  AdminUser,
  ApiKey,
  ApiKeyCreate,
  ApiKeyCreated,
  ApiKeyUpdate,
  AuditRow,
  CreateAdminUserBody,
  DashboardCounts,
  GatewayConfig,
  GatewayTestResult,
  GatewayUpsertBody,
  LoginResponse,
  Me,
  Merchant,
  MfaEnrolResponse,
  MfaResponse,
  PaymentDetail,
  PaymentRow,
  PaymentStatus,
  PspCode,
  SettlementFile,
  SettlementIngestSummary,
  SettlementPullResult,
  SettlementPullTrigger,
  SettlementRow,
  UpdateAdminUserBody,
  WebhookDelivery,
  WebhookDeliveryDetail,
  WebhookDeliveryStatus,
  WebhookEndpoint,
  WebhookEndpointCreate,
} from './types';

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
    public details?: unknown,
    public requestId?: string,
  ) {
    super(message);
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(path, {
    credentials: 'same-origin',
    ...init,
    headers: {
      Accept: 'application/json',
      ...(init.body !== undefined && !(init.body instanceof FormData)
        ? { 'Content-Type': 'application/json' }
        : {}),
      ...(init.headers ?? {}),
    },
  });
  const text = await res.text();
  const body = text ? safeParse(text) : null;
  if (!res.ok) {
    const env = body && typeof body === 'object' && body !== null
      ? (body as { error?: { code: string; message: string; details?: unknown; request_id?: string } }).error
      : undefined;
    throw new ApiError(
      res.status,
      env?.code ?? 'unknown',
      env?.message ?? res.statusText,
      env?.details,
      env?.request_id,
    );
  }
  return body as T;
}

function safeParse(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

function qs(params: Record<string, string | number | undefined | null>): string {
  const usp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null && v !== '') usp.set(k, String(v));
  }
  const s = usp.toString();
  return s ? `?${s}` : '';
}

// ---------------------------------------------------------------------------
// Auth
// ---------------------------------------------------------------------------

// Two demo modes coexist:
//   - "request_access": current default. Visitors submit a request and
//     wait for an emailed token. The login page CTA links to /demo/request.
//   - "shared_credentials": legacy path the demo box still ships. The
//     banner discloses a shared username/password until the demo branch
//     rebases onto request-access.
export type DemoInfo =
  | {
      enabled: true;
      mode: 'request_access';
      notice: string;
      request_url: string;
      token_login_url: string;
      default_ttl_hours: number;
    }
  | {
      enabled: true;
      mode: 'shared_credentials';
      username: string;
      password: string;
      notice: string;
    };

export type DemoAccessRequestBody = {
  name: string;
  email: string;
  company?: string | null;
  phone?: string | null;
  use_case?: string | null;
};

export const demo = {
  // 204 → null. Any non-OK is treated as "no demo banner" — we never
  // want a transient backend error to spam the login screen.
  info: async (): Promise<DemoInfo | null> => {
    try {
      const res = await fetch('/v1/demo/info', {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      });
      if (res.status !== 200) return null;
      return (await res.json()) as DemoInfo;
    } catch {
      return null;
    }
  },
  submitRequest: (body: DemoAccessRequestBody) =>
    request<{ id: string; status: 'pending' }>('/v1/demo/access-requests', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  tokenLogin: (token: string) =>
    request<{ user: { id: string; username: string; role: string } }>('/v1/demo/login', {
      method: 'POST',
      body: JSON.stringify({ token }),
    }),
};

// Admin-side demo access management.
export type DemoRequestRow = {
  id: string;
  email: string;
  name: string;
  company: string | null;
  phone: string | null;
  use_case: string | null;
  status: 'pending' | 'approved' | 'rejected';
  rejection_reason: string | null;
  decided_by: string | null;
  decided_at: string | null;
  created_at: string;
  token: {
    id: string;
    issued_at: string;
    expires_at: string;
    used_at: string | null;
    revoked_at: string | null;
  } | null;
};

export const demoAdmin = {
  list: (status?: 'pending' | 'approved' | 'rejected') => {
    const qs = status ? `?status=${encodeURIComponent(status)}` : '';
    return request<DemoRequestRow[]>(`/admin/demo-requests${qs}`);
  },
  approve: (id: string, ttl_hours: number) =>
    request<{
      request_id: string;
      token_id: string;
      token: string;
      expires_at: string;
    }>(`/admin/demo-requests/${id}/approve`, {
      method: 'POST',
      body: JSON.stringify({ ttl_hours }),
    }),
  reject: (id: string, reason: string | null) =>
    request<DemoRequestRow>(`/admin/demo-requests/${id}/reject`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    }),
  revoke: (id: string) =>
    request<{ request_id: string; token_id: string; revoked_at: string }>(
      `/admin/demo-requests/${id}/revoke`,
      { method: 'POST' },
    ),
};

export const auth = {
  login: (username: string, password: string) =>
    request<LoginResponse>('/admin/login', {
      method: 'POST',
      body: JSON.stringify({ username, password }),
    }),
  loginMfa: (challenge_token: string, code: string) =>
    request<MfaResponse>('/admin/login/mfa', {
      method: 'POST',
      body: JSON.stringify({ challenge_token, code }),
    }),
  logout: () => request<{ ok: true }>('/admin/logout', { method: 'POST' }),
  me: () => request<Me>('/admin/me'),
};

export const mfa = {
  enrol: () => request<MfaEnrolResponse>('/admin/mfa/enrol', { method: 'POST' }),
  confirm: (code: string) =>
    request<void>('/admin/mfa/confirm', {
      method: 'POST',
      body: JSON.stringify({ code }),
    }),
  disable: (password: string) =>
    request<void>('/admin/mfa/disable', {
      method: 'POST',
      body: JSON.stringify({ password }),
    }),
};

// ---------------------------------------------------------------------------
// Merchants + API keys
// ---------------------------------------------------------------------------

export const merchants = {
  list: () => request<Merchant[]>('/admin/merchants'),
  get: (id: string) => request<Merchant>(`/admin/merchants/${id}`),
  create: (body: { code: string; name: string; status?: string }) =>
    request<Merchant>('/admin/merchants', {
      method: 'POST',
      body: JSON.stringify({ status: 'active', ...body }),
    }),
  update: (id: string, body: { code: string; name: string; status: string }) =>
    request<Merchant>(`/admin/merchants/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  remove: (id: string) =>
    request<void>(`/admin/merchants/${id}`, { method: 'DELETE' }),
};

export const apiKeys = {
  list: (merchantId: string) =>
    request<ApiKey[]>(`/admin/merchants/${merchantId}/api-keys`),
  create: (merchantId: string, body: ApiKeyCreate) =>
    request<ApiKeyCreated>(`/admin/merchants/${merchantId}/api-keys`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  update: (merchantId: string, keyId: string, body: ApiKeyUpdate) =>
    request<ApiKey>(`/admin/merchants/${merchantId}/api-keys/${keyId}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  revoke: (merchantId: string, keyId: string) =>
    request<ApiKey>(
      `/admin/merchants/${merchantId}/api-keys/${keyId}/revoke`,
      { method: 'POST' },
    ),
};

// ---------------------------------------------------------------------------
// Gateways
// ---------------------------------------------------------------------------

export const gateways = {
  list: (merchantId: string) =>
    request<GatewayConfig[]>(`/admin/merchants/${merchantId}/gateways`),
  upsert: (merchantId: string, psp: PspCode, body: GatewayUpsertBody) =>
    request<GatewayConfig>(
      `/admin/merchants/${merchantId}/gateways/${psp}`,
      { method: 'PUT', body: JSON.stringify(body) },
    ),
  deactivate: (merchantId: string, psp: PspCode) =>
    request<void>(`/admin/merchants/${merchantId}/gateways/${psp}`, {
      method: 'DELETE',
    }),
  test: (merchantId: string, psp: PspCode) =>
    request<GatewayTestResult>(
      `/admin/merchants/${merchantId}/gateways/${psp}/test`,
      { method: 'POST' },
    ),
};

// ---------------------------------------------------------------------------
// Webhooks
// ---------------------------------------------------------------------------

export const webhookEndpoints = {
  list: (merchantId: string) =>
    request<WebhookEndpoint[]>(`/admin/merchants/${merchantId}/webhooks`),
  create: (merchantId: string, body: WebhookEndpointCreate) =>
    request<WebhookEndpoint>(`/admin/merchants/${merchantId}/webhooks`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  deactivate: (endpointId: string) =>
    request<void>(`/admin/webhooks/${endpointId}`, { method: 'DELETE' }),
};

export const deliveries = {
  list: (params: { status?: WebhookDeliveryStatus; limit?: number; offset?: number } = {}) =>
    request<WebhookDelivery[]>(`/admin/deliveries${qs(params)}`),
  get: (id: string) => request<WebhookDeliveryDetail>(`/admin/deliveries/${id}`),
  replay: (id: string) =>
    request<WebhookDelivery>(`/admin/deliveries/${id}/replay`, { method: 'POST' }),
  resolve: (id: string) =>
    request<WebhookDelivery>(`/admin/deliveries/${id}/resolve`, { method: 'POST' }),
};

// ---------------------------------------------------------------------------
// Reports
// ---------------------------------------------------------------------------

export const reports = {
  dashboard: (windowHours = 24) =>
    request<DashboardCounts>(`/admin/dashboard?window_hours=${windowHours}`),
  payments: (
    params: {
      merchant_id?: string;
      psp?: PspCode;
      status?: PaymentStatus;
      order_ref?: string;
      psp_ref?: string;
      msisdn?: string;
      amount_min?: number;
      amount_max?: number;
      created_after?: string;
      created_before?: string;
      limit?: number;
      offset?: number;
    } = {},
  ) => request<PaymentRow[]>(`/admin/payments${qs(params)}`),
  paymentDetail: (id: string) => request<PaymentDetail>(`/admin/payments/${id}`),
  recordRefund: (
    id: string,
    body: { amount_minor: number; reason?: string; psp_back_office_ref?: string; is_full: boolean },
  ) =>
    request<{
      payment_id: string;
      new_status: string;
      event_id: string;
      deliveries_enqueued: number;
    }>(`/admin/payments/${id}/record-refund`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  /** Re-poll the PSP for an in-flight payment. No-ops if already terminal. */
  repollPayment: (id: string) =>
    request<{ payment_id: string; status: string; transitioned: boolean }>(
      `/admin/payments/${id}/poll`,
      { method: 'POST' },
    ),
  /** Operator-attested manual transition for stuck payments. Admin-only. */
  forceResolvePayment: (
    id: string,
    body: { target_status: 'succeeded' | 'failed' | 'expired'; reason: string },
  ) =>
    request<{ payment_id: string; new_status: string; prior_status: string }>(
      `/admin/payments/${id}/force-resolve`,
      { method: 'POST', body: JSON.stringify(body) },
    ),
};

// ---------------------------------------------------------------------------
// Settlements (reconciliation)
// ---------------------------------------------------------------------------

export const settlements = {
  list: (params: { merchant_id?: string; psp?: PspCode; limit?: number; offset?: number } = {}) =>
    request<SettlementFile[]>(`/admin/settlements${qs(params)}`),
  get: (id: string) => request<SettlementFile>(`/admin/settlements/${id}`),
  rows: (
    id: string,
    params: { status_filter?: string; limit?: number; offset?: number } = {},
  ) => request<SettlementRow[]>(`/admin/settlements/${id}/rows${qs(params)}`),
  upload: async (input: {
    merchant_id: string;
    psp: PspCode;
    file: File;
    period_from?: string;
    period_to?: string;
  }): Promise<SettlementIngestSummary> => {
    const fd = new FormData();
    fd.set('merchant_id', input.merchant_id);
    fd.set('psp', input.psp);
    fd.set('file', input.file);
    if (input.period_from) fd.set('period_from', input.period_from);
    if (input.period_to) fd.set('period_to', input.period_to);
    return request<SettlementIngestSummary>('/admin/settlements', {
      method: 'POST',
      body: fd,
    });
  },
  supportedSources: () =>
    request<string[]>('/admin/settlements/sources/supported'),
  pull: (body: SettlementPullTrigger) =>
    request<SettlementPullResult>('/admin/settlements/pull', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
};

// ---------------------------------------------------------------------------
// Admin users (RBAC)
// ---------------------------------------------------------------------------

export const adminUsers = {
  list: () => request<AdminUser[]>('/admin/admin-users'),
  create: (body: CreateAdminUserBody) =>
    request<AdminUser>('/admin/admin-users', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  update: (id: string, body: UpdateAdminUserBody) =>
    request<AdminUser>(`/admin/admin-users/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  disable: (id: string) =>
    request<void>(`/admin/admin-users/${id}`, { method: 'DELETE' }),
  unlock: (id: string) =>
    request<{ id: string; username: string; failed_login_count: number; locked_until: string | null }>(
      `/admin/admin-users/${id}/unlock`,
      { method: 'POST' },
    ),
};

// ---------------------------------------------------------------------------
// Admin: merchant users (per-merchant CRUD + recovery)
// ---------------------------------------------------------------------------

export interface AdminMerchantUser {
  id: string;
  merchant_id: string;
  username: string;
  role: MerchantRole;
  status: 'active' | 'disabled';
  full_name: string;
  phone: string | null;
  mobile: string | null;
  email: string | null;
  email_verified_at: string | null;
  mobile_verified_at: string | null;
  mfa_enabled: boolean;
  last_login_at: string | null;
  created_at: string;
}

export interface AdminCreateMerchantUserBody {
  username: string;
  full_name: string;
  email?: string;
  mobile?: string;
  phone?: string;
  role: MerchantRole;
  password?: string;
}

export interface AdminUpdateMerchantUserBody {
  full_name?: string;
  email?: string;
  mobile?: string;
  phone?: string;
  role?: MerchantRole;
  status?: 'active' | 'disabled';
}

export interface AdminMerchantUserResetIssued {
  token: string;
  url: string;
  expires_at: string;
  purpose: 'reset' | 'email_verify' | 'mobile_verify';
}

export interface AdminCreateMerchantUserOut extends AdminMerchantUser {
  reset: AdminMerchantUserResetIssued | null;
}

export const adminMerchantUsers = {
  list: (merchantId: string) =>
    request<AdminMerchantUser[]>(`/admin/merchants/${merchantId}/users`),
  create: (merchantId: string, body: AdminCreateMerchantUserBody) =>
    request<AdminCreateMerchantUserOut>(`/admin/merchants/${merchantId}/users`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  update: (merchantId: string, userId: string, body: AdminUpdateMerchantUserBody) =>
    request<AdminMerchantUser>(`/admin/merchants/${merchantId}/users/${userId}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  disable: (merchantId: string, userId: string) =>
    request<void>(`/admin/merchants/${merchantId}/users/${userId}`, { method: 'DELETE' }),
  issueReset: (merchantId: string, userId: string) =>
    request<AdminMerchantUserResetIssued>(
      `/admin/merchants/${merchantId}/users/${userId}/issue-reset`,
      { method: 'POST' },
    ),
  clearMfa: (merchantId: string, userId: string) =>
    request<void>(`/admin/merchants/${merchantId}/users/${userId}/clear-mfa`, {
      method: 'POST',
    }),
};

// ---------------------------------------------------------------------------
// Audit
// ---------------------------------------------------------------------------

export const audit = {
  list: (
    params: {
      target_type?: string;
      target_id?: string;
      action_prefix?: string;
      limit?: number;
      offset?: number;
    } = {},
  ) => request<AuditRow[]>(`/admin/audit${qs(params)}`),
};

// ---------------------------------------------------------------------------
// Merchant self-service portal (mounted under /merchant/*)
// ---------------------------------------------------------------------------

export type MerchantRole = 'owner' | 'developer' | 'finance' | 'viewer';
export type MerchantOrgType = 'person' | 'company';

export interface MerchantMe {
  id: string;
  username: string;
  role: MerchantRole;
  full_name: string;
  phone: string | null;
  mobile: string | null;
  email: string | null;
  email_verified_at: string | null;
  mobile_verified_at: string | null;
  mfa_enabled: boolean;
  merchant: {
    id: string;
    code: string;
    name: string;
    type: MerchantOrgType;
  };
}

export type MerchantLoginResponse =
  | { requires_mfa: false; merchant_id: string }
  | { requires_mfa: true; challenge_token: string };

export interface MerchantUpdateMeBody {
  full_name?: string;
  phone?: string | null;
  mobile?: string | null;
  email?: string | null;
  preferred_lang?: string;
}

export interface MerchantChangePasswordBody {
  old_password: string;
  new_password: string;
  code?: string;
}

export const merchantAuth = {
  login: (merchant_code: string, username: string, password: string) =>
    request<MerchantLoginResponse>('/merchant/auth/login', {
      method: 'POST',
      body: JSON.stringify({ merchant_code, username, password }),
    }),
  loginMfa: (challenge_token: string, code: string) =>
    request<{ ok: true; merchant_id: string }>('/merchant/auth/login/mfa', {
      method: 'POST',
      body: JSON.stringify({ challenge_token, code }),
    }),
  logout: () =>
    fetch('/merchant/auth/logout', {
      method: 'POST',
      credentials: 'same-origin',
    }).then(() => undefined),
  me: () => request<MerchantMe>('/merchant/auth/me'),
  updateMe: (body: MerchantUpdateMeBody) =>
    request<MerchantMe>('/merchant/auth/me', {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  changePassword: (body: MerchantChangePasswordBody) =>
    request<void>('/merchant/auth/change-password', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  forgotPassword: (body: {
    merchant_code: string;
    username: string;
    channel: 'email' | 'sms';
  }) =>
    request<void>('/merchant/auth/forgot-password', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  resetPassword: (body: {
    merchant_code: string;
    username: string;
    token: string;
    new_password: string;
  }) =>
    request<void>('/merchant/auth/reset-password', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  verifyEmail: (token: string) =>
    request<void>('/merchant/auth/verify-email', {
      method: 'POST',
      body: JSON.stringify({ token }),
    }),
  verifyMobile: (code: string) =>
    request<void>('/merchant/auth/verify-mobile', {
      method: 'POST',
      body: JSON.stringify({ code }),
    }),
};

export const merchantPayments = {
  list: (params: { psp?: PspCode; status?: PaymentStatus; limit?: number; offset?: number } = {}) =>
    request<PaymentRow[]>(`/merchant/payments${qs(params)}`),
  get: (id: string) => request<PaymentDetail>(`/merchant/payments/${id}`),
};

export const merchantApiKeys = {
  list: () => request<ApiKey[]>('/merchant/api-keys'),
  create: (body: ApiKeyCreate) =>
    request<ApiKeyCreated>('/merchant/api-keys', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  update: (keyId: string, body: ApiKeyUpdate) =>
    request<ApiKey>(`/merchant/api-keys/${keyId}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  revoke: (keyId: string) =>
    request<ApiKey>(`/merchant/api-keys/${keyId}/revoke`, { method: 'POST' }),
};

export const merchantWebhooks = {
  list: () => request<WebhookEndpoint[]>('/merchant/webhook-endpoints'),
  create: (body: WebhookEndpointCreate) =>
    request<WebhookEndpoint>('/merchant/webhook-endpoints', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  deactivate: (id: string) =>
    request<void>(`/merchant/webhook-endpoints/${id}`, { method: 'DELETE' }),
};

export const merchantSettlements = {
  list: (params: { psp?: PspCode; limit?: number; offset?: number } = {}) =>
    request<SettlementFile[]>(`/merchant/settlements${qs(params)}`),
  get: (id: string) => request<SettlementFile>(`/merchant/settlements/${id}`),
  rows: (
    id: string,
    params: { status_filter?: string; limit?: number; offset?: number } = {},
  ) => request<SettlementRow[]>(`/merchant/settlements/${id}/rows${qs(params)}`),
};

// ---------------------------------------------------------------------------
// Merchant: own org
// ---------------------------------------------------------------------------

export interface MerchantOrg {
  id: string;
  code: string;
  name: string;
  type: MerchantOrgType;
  status: 'active' | 'suspended' | 'disabled';
  created_at: string;
}

export const merchantOrg = {
  get: () => request<MerchantOrg>('/merchant/org'),
  update: (body: { name?: string; type?: MerchantOrgType }) =>
    request<MerchantOrg>('/merchant/org', {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
};

// ---------------------------------------------------------------------------
// Merchant: peer users (owner only)
// ---------------------------------------------------------------------------

export interface MerchantPeer {
  id: string;
  username: string;
  role: MerchantRole;
  status: 'active' | 'disabled';
  full_name: string;
  email: string | null;
  mobile: string | null;
  phone: string | null;
  mfa_enabled: boolean;
  last_login_at: string | null;
  created_at: string;
}

export interface InviteMerchantPeerBody {
  username: string;
  full_name: string;
  email?: string;
  mobile?: string;
  phone?: string;
  role: MerchantRole;
}

export interface UpdateMerchantPeerBody {
  full_name?: string;
  email?: string;
  mobile?: string;
  phone?: string;
  role?: MerchantRole;
  status?: 'active' | 'disabled';
}

export const merchantUsers = {
  list: () => request<MerchantPeer[]>('/merchant/users'),
  invite: (body: InviteMerchantPeerBody) =>
    request<MerchantPeer>('/merchant/users', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  update: (id: string, body: UpdateMerchantPeerBody) =>
    request<MerchantPeer>(`/merchant/users/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  disable: (id: string) =>
    request<void>(`/merchant/users/${id}`, { method: 'DELETE' }),
  issueReset: (id: string) =>
    request<{ sent_to_channel: 'email' | 'sms'; expires_at: string }>(
      `/merchant/users/${id}/issue-reset`,
      { method: 'POST' },
    ),
  clearMfa: (id: string, code: string) =>
    request<void>(`/merchant/users/${id}/clear-mfa`, {
      method: 'POST',
      body: JSON.stringify({ code }),
    }),
};

// ---------------------------------------------------------------------------
// Merchant: own PSP gateways
// ---------------------------------------------------------------------------

export interface MerchantGatewayOut {
  id: string;
  psp_code: PspCode;
  env: string;
  version: number;
  active: boolean;
  payload_keys: string[];
  created_at: string;
  updated_at: string;
}

export const merchantGateways = {
  list: () => request<MerchantGatewayOut[]>('/merchant/gateways'),
  upsert: (psp: PspCode, body: GatewayUpsertBody) =>
    request<MerchantGatewayOut>(`/merchant/gateways/${psp}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    }),
  deactivate: (psp: PspCode) =>
    request<void>(`/merchant/gateways/${psp}`, { method: 'DELETE' }),
  test: (psp: PspCode) =>
    request<GatewayTestResult>(`/merchant/gateways/${psp}/test`, { method: 'POST' }),
};
